import { useSchool } from '@/lib/useSchool';
import { naira } from '@/lib/format';
import { useApplication, useStatus } from '../shared';
import { AdmissionCard } from './AdmissionCard';
import { Actions, Body, Hero, Loading, Panel, Pill, Primary, Rows, Screen, Secondary, Tracker, shortDate, type TrackStep } from '../ui';

/** Landing before an application exists, then the application tracker. */
export function OverviewPage() {
  const status = useStatus();
  const app = useApplication();
  const school = useSchool();
  const s = status.data;
  const env = app.data;
  const a = env?.application ?? null;

  if (status.isLoading || app.isLoading) return <Screen><Hero withSchool title="Loading…" /><Loading /></Screen>;

  /* ── not started: "Apply in 3 steps" ── */
  if (!a) {
    const cycle = env?.cycles?.[0];
    return (
      <Screen>
        <div className="bg-[#0A2540] text-white text-center px-6 py-14 sm:rounded-2xl">
          {school.logo ? <img src={school.logo} alt="" className="w-16 h-16 rounded-2xl object-cover bg-white mx-auto mb-4" /> : null}
          <h1 className="text-3xl font-extrabold uppercase tracking-wide">{school.name}</h1>
          <p className="text-slate-300 mt-2">{cycle ? `Admissions open · ${cycle.name}` : 'Admissions'}</p>
          {school.motto && <p className="text-slate-400 text-sm mt-1 italic">{school.motto}</p>}
        </div>
        <Body>
          {cycle ? (
            <>
              <h2 className="text-2xl font-extrabold text-[#0A2540]">Apply in 3 steps</h2>
              {['Child and parent details', 'Upload documents', 'Entrance exam'].map((t, i) => (
                <Panel key={t}><p className="text-lg font-bold text-[#0A2540]">{i + 1}. {t}</p></Panel>
              ))}
              {cycle.form_fee > 0 && <p className="text-sm text-slate-600">Application fee: <b>{naira(cycle.form_fee)}</b>{cycle.closes_on ? ` · closes ${shortDate(cycle.closes_on)}` : ''}</p>}
              {cycle.instructions && <Panel tone="blue"><p className="text-sm text-slate-700 whitespace-pre-wrap">{cycle.instructions}</p></Panel>}
              <Actions><Primary to="/applicant/application">Start application</Primary><p className="text-center text-xs text-slate-500">Your answers save as you go.</p></Actions>
            </>
          ) : (
            <Panel><p className="font-bold text-slate-900">Admissions are closed right now</p><p className="text-sm text-slate-600 mt-1">{school.name} is not taking applications at the moment. Please check back later.</p></Panel>
          )}
        </Body>
      </Screen>
    );
  }

  /* ── tracker ── */
  const t = s?.timeline;
  const st = s?.status ?? a.status;
  const paid = s?.fee?.paid ?? a.fee.paid;
  const examDone = !!t?.exam_taken_at || ['exam_taken', 'admitted', 'accepted', 'enrolled', 'waitlisted', 'rejected'].includes(st);
  const decided = ['enrolled', 'admitted', 'accepted', 'waitlisted', 'rejected', 'declined'].includes(st);
  const submitted = st !== 'draft';
  const examBooked = !!s?.exam || !!t?.exam_booked_at;

  const steps: TrackStep[] = [
    { label: 'Submitted', date: shortDate(t?.submitted_at), state: submitted ? 'done' : 'current', note: submitted ? undefined : 'Finish and submit your form' },
    ...(a.fee.amount > 0 ? [{ label: 'Application fee paid', date: shortDate(t?.paid_at), state: paid ? 'done' : submitted ? 'current' : 'todo' } as TrackStep] : []),
    { label: examDone ? 'Exam taken' : 'Exam scheduled', date: examDone ? shortDate(t?.exam_taken_at) : t?.exam_at ? shortDate(t.exam_at, true) : null, state: examDone ? 'done' : examBooked ? 'current' : 'todo' },
    { label: st === 'waitlisted' ? 'Waitlisted' : st === 'rejected' ? 'Not admitted' : st === 'enrolled' ? 'Admitted' : 'Decision', date: shortDate(t?.decision_at ?? t?.admitted_at), state: decided ? 'done' : examDone ? 'current' : 'todo' },
  ];

  const next: { title: string; text: string; to?: string; cta?: string } | null =
    st === 'draft' ? { title: 'Next: finish your application', text: 'Complete the form and upload your documents.', to: '/applicant/application', cta: 'Continue application' }
      : !paid ? { title: 'Next: pay the application fee', text: `${naira(a.fee.amount)}. Your entrance exam is booked as soon as it is paid.`, to: '/applicant/payment', cta: `Pay ${naira(a.fee.amount)}` }
      : s?.next_action === 'upload_document' ? { title: 'The school needs a document', text: 'Upload it so they can continue.', to: '/applicant/documents', cta: 'Upload now' }
      : s?.next_action === 'take_exam' ? { title: 'Next: take your entrance exam', text: `${s.exam?.venue ? `${s.exam.venue}, or ` : ''}on this phone${s.exam?.scheduled_at ? ` from ${shortDate(s.exam.scheduled_at, true)}` : ' now'}.`, to: '/applicant/exam', cta: 'Take entrance exam' }
      : st === 'exam_scheduled' ? { title: 'Your entrance exam is booked', text: s?.exam?.scheduled_at ? `It opens ${shortDate(s.exam.scheduled_at, true)}.` : 'It will open soon.', to: '/applicant/exam', cta: 'View exam details' }
      : st === 'exam_taken' ? { title: 'Exam done', text: 'The school will tell you its decision soon.', to: '/applicant/result', cta: 'See my result' }
      : st === 'paid' || st === 'submitted' ? { title: 'Under review', text: 'The school is reviewing your application. You will be told when your exam is booked.' }
      : null;

  return (
    <Screen>
      <Hero withSchool kicker={`Application ${a.reference}`} title={<>{[a.first_name, a.last_name].filter(Boolean).join(' ') || 'Your application'}{a.class_applied ? ` · ${a.class_applied.name}` : ''}</>} />
      <Body>
        {s?.admission && <AdmissionCard admission={s.admission} name={s.name} />}

        {s?.pending_payment?.review === 'pending' && <Panel tone="amber"><p className="font-bold text-slate-900">Receipt received</p><p className="text-sm text-slate-700">The school is checking your payment receipt. We'll tell you once it is confirmed.</p></Panel>}
        {s?.pending_payment?.review === 'rejected' && <Panel tone="red"><p className="font-bold text-red-800">Payment not confirmed</p><p className="text-sm text-red-700">{s.pending_payment.rejection_reason}</p></Panel>}

        {st === 'waitlisted' && (
          <Panel><div className="flex items-center justify-between gap-3"><p className="text-lg font-bold">Status</p><Pill tone="amber">Waitlisted</Pill></div>
            <p className="text-sm text-slate-600 mt-2">{a.class_applied?.name ?? 'This class'} is full for now. We will message you if a place opens.</p>{s?.decision?.note && <p className="text-sm text-slate-600 mt-1">{s.decision.note}</p>}</Panel>
        )}
        {st === 'rejected' && <Panel><p className="font-bold">Not admitted this time</p><p className="text-sm text-slate-600 mt-1">{s?.decision?.note ?? 'The school was unable to offer a place this time.'}</p></Panel>}

        {!s?.admission && <Panel><Tracker steps={steps} /></Panel>}

        {next && (
          <Panel className="border-green-600">
            <p className="text-lg font-bold text-[#0A2540]">{next.title}</p>
            <p className="text-sm text-slate-600 mt-0.5">{next.text}</p>
          </Panel>
        )}

        <Panel>
          <Rows rows={[
            ['Applicant', [a.first_name, a.last_name].filter(Boolean).join(' ') || '—'],
            ['Class', a.class_applied?.name ?? '—'],
            ['Parent', a.guardian_name ? `${a.guardian_name}${a.guardian_phone ? ` · ${a.guardian_phone}` : ''}` : '—'],
            ['Documents', `${a.documents.length} of ${(a.required_documents ?? []).length || a.documents.length}`],
            ['Application fee', a.fee.amount > 0 ? <span>{naira(a.fee.amount)} <Pill tone={paid ? 'green' : 'amber'}>{paid ? 'Paid' : 'Unpaid'}</Pill></span> : 'Free'],
          ]} />
        </Panel>

        {next?.to && (
          <Actions>
            <Primary to={next.to}>{next.cta}</Primary>
            {st !== 'draft' && <Secondary to="/applicant/notifications">Messages from the school</Secondary>}
          </Actions>
        )}
      </Body>
    </Screen>
  );
}
